/**
 * File System Access API storage layer.
 * Uses IndexedDB to persist the FileSystemFileHandle across page reloads
 * (localStorage cannot store complex objects like handles).
 */

const IDB_NAME = 'budget-app-meta';
const IDB_STORE = 'handles';
const HANDLE_KEY = 'storage-file';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function saveFileHandle(handle) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).put(handle, HANDLE_KEY);
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
}

export async function loadFileHandle() {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).get(HANDLE_KEY);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

export async function clearFileHandle() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).delete(HANDLE_KEY);
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
}

/** Verify that the handle is still valid and we have permission. */
export async function verifyPermission(handle, readWrite = true) {
  const opts = { mode: readWrite ? 'readwrite' : 'read' };
  if ((await handle.queryPermission(opts)) === 'granted') return true;
  if ((await handle.requestPermission(opts)) === 'granted') return true;
  return false;
}

/** Read JSON from a FileSystemFileHandle. Returns null on failure. */
export async function readFromFile(handle) {
  try {
    const ok = await verifyPermission(handle, false);
    if (!ok) return null;
    const file = await handle.getFile();
    const text = await file.text();
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Write JSON to a FileSystemFileHandle. Returns true on success. */
export async function writeToFile(handle, data) {
  try {
    const ok = await verifyPermission(handle, true);
    if (!ok) return false;
    const writable = await handle.createWritable();
    await writable.write(JSON.stringify(data, null, 2));
    await writable.close();
    return true;
  } catch {
    return false;
  }
}

/** Ask user to pick (or create) a .json file for storage. Returns handle or null. */
export async function pickStorageFile() {
  try {
    const handle = await window.showSaveFilePicker({
      suggestedName: 'budget-data.json',
      types: [{ description: 'JSON', accept: { 'application/json': ['.json'] } }],
    });
    return handle;
  } catch {
    // User cancelled
    return null;
  }
}

/**
 * Open an existing .json file for storage (load-and-link flow).
 * Uses showOpenFilePicker so the OS shows an "Open" dialog rather than
 * a "Save As" dialog — picking an existing file feels like loading it,
 * not overwriting it.
 */
export async function pickExistingStorageFile() {
  try {
    const [handle] = await window.showOpenFilePicker({
      types: [{ description: 'JSON', accept: { 'application/json': ['.json'] } }],
      multiple: false,
    });
    return handle || null;
  } catch {
    // User cancelled
    return null;
  }
}

export const isFileSystemSupported = () => typeof window !== 'undefined' && 'showSaveFilePicker' in window;
